import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMasterData } from '../../hooks/useMasterData';
import { useAuth } from '../../hooks/useAuth';
import { useDemoMode } from '../../hooks/useDemoMode';
// import { CompanyService } from '../../services/companyService';
import {
  BufferTimeSettings,
  CompanyOCRSettings,
  BusinessPartner,
  BusinessPartnerDataSource,
  BusinessPartnerCategory,
  VehicleModel,
  CompanyVehicleModelSettings,
  TransportFeeSettings,
  TransportFeeCalculationMethod,
  CompanyPartnerSelection,
  Case,
  Manufacturer,
  DriverScheduleVisibility,
  DriverScheduleEditPermission,
  DriverTransportFeeVisibilitySettings,
  DEFAULT_DRIVER_TRANSPORT_FEE_VISIBILITY_SETTINGS,
} from '../../types';
import { DEFAULT_BUFFER_TIME_SETTINGS } from '../../utils/timePickerDefaults';
import { Button, TimeSelector, Toggle } from '../../components/ui';
import { VehicleModelDetail } from '../../components/vehicle/VehicleModelDetail';
import logger from '../../utils/logger';
import {
  CogIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  TagIcon,
  CurrencyYenIcon,
  CalendarDaysIcon,
  RectangleStackIcon,
  WrenchScrewdriverIcon,
  LinkIcon,
  EnvelopeIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { PageTitle } from '../../components/ui';
import { FeatureDemoNotice } from '../../components/common/DemoModeNotice';
import { DemoFeature } from '../../types/demoMode';
import { DEMO_COMPANY_ID } from '../../constants/company';
import { DEFAULT_OCR_SETTINGS, DEFAULT_TRANSPORT_FEE_SETTINGS } from '../../constants/settings';
import {
  getCompanyScheduleVisibility,
  updateCompanyScheduleVisibility,
} from '../../services/supabaseScheduleService';
import ScheduleVisibilitySettingsForm from '../../components/forms/ScheduleVisibilitySettingsForm';
import DriverScheduleEditPermissionForm from '../../components/forms/DriverScheduleEditPermissionForm';
import {
  updateCompany,
  getCompanyById,
  supabaseCompanyService,
} from '../../services/supabase/supabaseCompanyService';
import { dailyReportService } from '../../services/dailyReportService';
import { getPowertrainLabel, getPowertrainColorClasses } from '../../utils/vehicleModelUtils';
import DriverVisibilitySettingsForm from '../../components/forms/DriverVisibilitySettingsForm';

// タブグループの定義
type TabGroup = 'masterData' | 'business' | 'integration';
type TabType =
  | 'partners'
  | 'vehicles'
  | 'buffer'
  | 'timeLimit'
  | 'transportFee'
  | 'driverVisibility'
  | 'schedule'
  | 'ocr'
  | 'dailyReport';

interface TabGroupConfig {
  id: TabGroup;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  tabs: Array<{
    id: TabType;
    label: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  }>;
}

const TAB_GROUPS: TabGroupConfig[] = [
  {
    id: 'masterData',
    label: 'マスタデータ設定',
    icon: RectangleStackIcon,
    tabs: [
      { id: 'partners', label: '取引先選択', icon: UserGroupIcon },
      { id: 'vehicles', label: '車両モデル選択', icon: TagIcon },
    ],
  },
  {
    id: 'business',
    label: '業務設定',
    icon: WrenchScrewdriverIcon,
    tabs: [
      { id: 'buffer', label: 'バッファ時間設定', icon: ClockIcon },
      { id: 'transportFee', label: '陸送費設定', icon: CurrencyYenIcon },
      { id: 'driverVisibility', label: 'ドライバー表示設定', icon: EyeIcon },
      { id: 'schedule', label: 'スケジュール設定', icon: CalendarDaysIcon },
      { id: 'timeLimit', label: '時間制限設定', icon: CogIcon },
    ],
  },
  {
    id: 'integration',
    label: 'システム連携',
    icon: LinkIcon,
    tabs: [
      { id: 'ocr', label: 'OCR設定', icon: PhotoIcon },
      { id: 'dailyReport', label: '日次レポート', icon: EnvelopeIcon },
    ],
  },
];

const SettingsPage: React.FC = () => {
  const [activeGroup, setActiveGroup] = useState<TabGroup>('masterData');
  const [activeTab, setActiveTab] = useState<TabType>('partners');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 表示設定の状態管理
  // const _companyService = useMemo(() => new CompanyService(), []);
  const { user: _user } = useAuth();

  const {
    businessPartners,
    vehicleModels,
    manufacturers,
    cases: activeCases,
    loading,
    getCompanyVehicleModelSettingsByCompanyId,
    updateCompanyVehicleModelSettings,
    addCompanyVehicleModelSettings,
    addCompanyPartnerSelection,
    updateCompanyPartnerSelection,
    getCompanyPartnerSelectionsByCompanyId,
    getAllBusinessPartners,
  } = useMasterData();

  const { user } = useAuth();
  const { isEnabled: isDemoMode, getMessage: getDemoMessage } = useDemoMode();

  const [bufferSettingsState, setBufferSettingsState] = useState<BufferTimeSettings>(
    DEFAULT_BUFFER_TIME_SETTINGS
  );
  const [ocrSettingsState, setOCRSettingsState] = useState<CompanyOCRSettings>(
    DEFAULT_OCR_SETTINGS as CompanyOCRSettings
  );
  const [transportFeeSettings, setTransportFeeSettings] = useState<TransportFeeSettings>(
    DEFAULT_TRANSPORT_FEE_SETTINGS
  );

  // FL-114: 陸送員スケジュール公開範囲設定
  const [scheduleVisibility, setScheduleVisibility] = useState<DriverScheduleVisibility>(
    DriverScheduleVisibility.ADMIN_ONLY
  );

  // FL-115: 陸送員スケジュール編集権限設定
  const [scheduleEditPermission, setScheduleEditPermission] =
    useState<DriverScheduleEditPermission>(DriverScheduleEditPermission.ADMIN_ONLY);

  // FL-118: 日次レポート設定
  const [dailyReportSettings, setDailyReportSettings] = useState<{
    reportType: 'case_list' | 'driver_schedule' | 'combined';
    sendTime: string;
    sendToEmails: string[];
    includeCsv: boolean;
    includePdf: boolean;
    dateRangeDays: number;
    isActive: boolean;
    emailSubjectTemplate?: string;
    emailBodyTemplate?: string;
  }>({
    reportType: 'combined',
    sendTime: '08:00',
    sendToEmails: [],
    includeCsv: true,
    includePdf: true,
    dateRangeDays: 1,
    isActive: false,
  });

  // FL-229: ドライバー表示設定
  const [driverVisibilitySettings, setDriverVisibilitySettings] =
    useState<DriverTransportFeeVisibilitySettings>(
      DEFAULT_DRIVER_TRANSPORT_FEE_VISIBILITY_SETTINGS
    );

  // システム設定用の全取引先データ（フィルタリングなし）
  const [allPartnersForSettings, setAllPartnersForSettings] = useState<BusinessPartner[]>([]);

  // partnersタブが選択された時に全取引先を取得
  useEffect(() => {
    if (activeTab === 'partners') {
      const loadAllPartners = async () => {
        try {
          logger.info('🔄 システム設定：全取引先取得開始');
          const allPartners = await getAllBusinessPartners();
          setAllPartnersForSettings(allPartners);
          logger.info('✅ システム設定：全取引先取得完了', { count: allPartners.length });
        } catch (error) {
          logger.error('❌ システム設定：全取引先取得エラー:', error);
          // エラー時は通常のbusinessPartnersを使用
          setAllPartnersForSettings(businessPartners);
        }
      };

      loadAllPartners();
    }
  }, [activeTab, getAllBusinessPartners, businessPartners]);

  // FL-114 & FL-115: スケジュール設定タブの初期化
  useEffect(() => {
    if (activeTab === 'schedule' && user?.companyId) {
      const loadScheduleSettings = async () => {
        try {
          logger.info('🔄 スケジュール設定取得開始', { companyId: user.companyId });

          // FL-114: スケジュール可視性設定を取得
          const visibility = await getCompanyScheduleVisibility(user.companyId!);
          setScheduleVisibility(visibility);

          // FL-115: スケジュール編集権限設定を取得
          const company = await getCompanyById(user.companyId!);
          const editPermission =
            company?.driverScheduleEditPermission ?? DriverScheduleEditPermission.ADMIN_ONLY;
          setScheduleEditPermission(editPermission);

          logger.info('✅ スケジュール設定取得完了', { visibility, editPermission });
        } catch (error) {
          logger.error('❌ スケジュール設定取得エラー:', error);
          // エラー時はデフォルト値を使用
          setScheduleVisibility(DriverScheduleVisibility.ADMIN_ONLY);
          setScheduleEditPermission(DriverScheduleEditPermission.ADMIN_ONLY);
        }
      };

      loadScheduleSettings();
    }
  }, [activeTab, user?.companyId]);

  // FL-118: 日次レポート設定タブの初期化
  useEffect(() => {
    if (activeTab === 'dailyReport' && user?.companyId) {
      const loadDailyReportSettings = async () => {
        try {
          logger.info('🔄 日次レポート設定取得開始', { companyId: user.companyId });

          const settings = await dailyReportService.getCompanySettings(user.companyId!);
          if (settings) {
            setDailyReportSettings({
              reportType: settings.reportType,
              sendTime: settings.sendTime,
              sendToEmails: settings.sendToEmails,
              includeCsv: settings.includeCsv,
              includePdf: settings.includePdf,
              dateRangeDays: settings.dateRangeDays,
              isActive: settings.isActive,
              emailSubjectTemplate: settings.emailSubjectTemplate,
              emailBodyTemplate: settings.emailBodyTemplate,
            });
          }

          logger.info('✅ 日次レポート設定取得完了', { settings });
        } catch (error) {
          logger.error('❌ 日次レポート設定取得エラー:', error);
        }
      };

      loadDailyReportSettings();
    }
  }, [activeTab, user?.companyId]);

  // FL-229: ドライバー表示設定タブの初期化
  useEffect(() => {
    if (activeTab === 'driverVisibility' && user?.companyId) {
      const loadDriverVisibilitySettings = async () => {
        try {
          logger.info('🔄 ドライバー表示設定取得開始', { companyId: user.companyId });

          const settings = await supabaseCompanyService.getDriverTransportFeeVisibilitySettings(
            user.companyId!
          );
          setDriverVisibilitySettings(settings);

          logger.info('✅ ドライバー表示設定取得完了', { settings });
        } catch (error) {
          logger.error('❌ ドライバー表示設定取得エラー:', error);
          // エラー時はデフォルト値を使用
          setDriverVisibilitySettings(DEFAULT_DRIVER_TRANSPORT_FEE_VISIBILITY_SETTINGS);
        }
      };

      loadDriverVisibilitySettings();
    }
  }, [activeTab, user?.companyId]);

  const handleBufferSettingsChange = (updates: Partial<BufferTimeSettings>) => {
    setBufferSettingsState(prev => ({ ...prev, ...updates }));
  };

  const handleOCRSettingsChange = (updates: Partial<CompanyOCRSettings>) => {
    setOCRSettingsState(prev => ({ ...prev, ...updates }) as CompanyOCRSettings);
  };

  const handleTransportFeeSettingsChange = (updates: Partial<TransportFeeSettings>) => {
    setTransportFeeSettings(prev => ({ ...prev, ...updates }));
  };

  // FL-114 & FL-115: スケジュール設定の保存
  const handleScheduleSettingsSave = async () => {
    if (!user?.companyId) {
      setMessage({ type: 'error', text: '会社情報が取得できません。' });
      return;
    }

    setIsLoading(true);
    try {
      logger.info('🔄 スケジュール設定保存開始', {
        companyId: user.companyId,
        visibility: scheduleVisibility,
        editPermission: scheduleEditPermission,
      });

      // FL-114: スケジュール可視性設定を更新
      await updateCompanyScheduleVisibility(user.companyId!, scheduleVisibility);

      // FL-115: スケジュール編集権限設定を更新
      await updateCompany(user.companyId!, {
        driverScheduleEditPermission: scheduleEditPermission,
      });

      setMessage({ type: 'success', text: 'スケジュール設定を保存しました。' });
      logger.info('✅ スケジュール設定保存完了');
    } catch (error) {
      logger.error('❌ スケジュール設定保存エラー:', error);
      setMessage({ type: 'error', text: 'スケジュール設定の保存に失敗しました。' });
    } finally {
      setIsLoading(false);
    }
  };

  // FL-115: スケジュール編集権限設定の保存
  const handleEditPermissionSave = async () => {
    if (!user?.companyId) {
      setMessage({ type: 'error', text: '会社情報が取得できません。' });
      return;
    }

    setIsLoading(true);
    try {
      logger.info('🔄 スケジュール編集権限設定保存開始', {
        companyId: user.companyId,
        editPermission: scheduleEditPermission,
      });

      await updateCompany(user.companyId!, {
        driverScheduleEditPermission: scheduleEditPermission,
      });

      setMessage({ type: 'success', text: 'スケジュール編集権限設定を保存しました。' });
      logger.info('✅ スケジュール編集権限設定保存完了');
    } catch (error) {
      logger.error('❌ スケジュール編集権限設定保存エラー:', error);
      setMessage({ type: 'error', text: 'スケジュール編集権限設定の保存に失敗しました。' });
    } finally {
      setIsLoading(false);
    }
  };

  // FL-229: ドライバー表示設定の保存
  const handleDriverVisibilitySave = async () => {
    if (!user?.companyId) {
      setMessage({ type: 'error', text: '会社情報が取得できません。' });
      return;
    }

    setIsLoading(true);
    try {
      logger.info('🔄 ドライバー表示設定保存開始', {
        companyId: user.companyId,
        settings: driverVisibilitySettings,
      });

      await supabaseCompanyService.updateDriverTransportFeeVisibilitySettings(
        user.companyId!,
        driverVisibilitySettings
      );

      setMessage({ type: 'success', text: 'ドライバー表示設定を保存しました。' });
      logger.info('✅ ドライバー表示設定保存完了');
    } catch (error) {
      logger.error('❌ ドライバー表示設定保存エラー:', error);
      setMessage({ type: 'error', text: 'ドライバー表示設定の保存に失敗しました。' });
    } finally {
      setIsLoading(false);
    }
  };

  // FL-118: 日次レポート設定の保存
  const handleDailyReportSettingsSave = async () => {
    if (!user?.companyId) {
      setMessage({ type: 'error', text: '会社情報が取得できません。' });
      return;
    }

    setIsLoading(true);
    try {
      logger.info('🔄 日次レポート設定保存開始', {
        companyId: user.companyId,
        settings: dailyReportSettings,
      });

      await dailyReportService.saveCompanySettings({
        companyId: user.companyId!,
        ...dailyReportSettings,
        updatedBy: user.id,
      });

      setMessage({ type: 'success', text: '日次レポート設定を保存しました。' });
      logger.info('✅ 日次レポート設定保存完了');
    } catch (error) {
      logger.error('❌ 日次レポート設定保存エラー:', error);
      setMessage({ type: 'error', text: '日次レポート設定の保存に失敗しました。' });
    } finally {
      setIsLoading(false);
    }
  };

  // FL-118: テストメール送信
  const handleSendTestEmail = async (email: string) => {
    // デモモード時の制限チェック
    if (isDemoMode) {
      setMessage({
        type: 'error',
        text: getDemoMessage(DemoFeature.EMAIL_SEND),
      });
      return;
    }

    if (!user?.companyId) {
      setMessage({
        type: 'error',
        text: '会社情報が取得できません。ページを再読み込みしてください。',
      });
      return;
    }

    setIsLoading(true);
    try {
      logger.info('🔄 テストメール送信開始', { companyId: user.companyId, email });

      const result = await dailyReportService.sendTestEmail(user.companyId!, email);

      if (result.success) {
        setMessage({
          type: 'success',
          text: `✅ テストメールを ${email} に正常に送信しました。受信ボックスをご確認ください。`,
        });
        logger.info('✅ テストメール送信完了');
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'テストメールの送信に失敗しました。',
        });
        logger.error('❌ テストメール送信失敗:', result.error);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : '予期しないエラーが発生しました';
      logger.error('❌ テストメール送信エラー:', error);
      setMessage({
        type: 'error',
        text: `テストメール送信エラー: ${errorMessage}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // キュー処理機能は新しいアーキテクチャでは不要（メール送信は即座に実行されるため）

  // FL-118: 日次レポート手動実行
  const handleExecuteDailyReportNow = async () => {
    // デモモード時の制限チェック
    if (isDemoMode) {
      setMessage({
        type: 'error',
        text: getDemoMessage(DemoFeature.EMAIL_SEND),
      });
      return;
    }

    if (!user?.companyId) {
      setMessage({ type: 'error', text: '会社情報が取得できません。' });
      return;
    }

    setIsLoading(true);
    try {
      logger.info('🔄 日次レポート手動実行開始', { companyId: user.companyId });

      // dailyReportExecutorを使用して実行
      const { dailyReportExecutor } = await import('../../services/dailyReportExecutor');
      const result = await dailyReportExecutor.executeForCompany(user.companyId, {
        forceExecute: true,
        testMode: false,
      });

      if (result.success) {
        setMessage({
          type: 'success',
          text: `日次レポートを送信しました。${result.recipientsCount}件のメールアドレスに送信されました。`,
        });
        logger.info('✅ 日次レポート手動実行完了', {
          executionTimeMs: result.executionTimeMs,
          filesGenerated: result.filesGenerated,
          recipientsCount: result.recipientsCount,
        });
      } else {
        setMessage({ type: 'error', text: `日次レポートの送信に失敗しました: ${result.error}` });
        logger.error('❌ 日次レポート手動実行失敗', { error: result.error });
      }
    } catch (error) {
      logger.error('❌ 日次レポート手動実行エラー:', error);
      const errorMessage = error instanceof Error ? error.message : '不明なエラーが発生しました';
      setMessage({ type: 'error', text: `日次レポートの実行に失敗しました: ${errorMessage}` });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      if (activeTab === 'buffer') {
        // TODO: バッファ設定の保存処理を実装
        setMessage({ type: 'success', text: 'バッファ時間設定を保存しました' });
      } else if (activeTab === 'ocr') {
        // TODO: OCR設定の保存処理を実装
        setMessage({ type: 'success', text: 'OCR設定を保存しました' });
      }
    } catch (error) {
      logger.error('Failed to save settings:', error);
      setMessage({ type: 'error', text: '設定の保存に失敗しました' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    if (activeTab === 'buffer') {
      setBufferSettingsState(DEFAULT_BUFFER_TIME_SETTINGS);
      setMessage({ type: 'success', text: 'デフォルト設定に戻しました' });
    } else if (activeTab === 'ocr') {
      setOCRSettingsState(DEFAULT_OCR_SETTINGS as CompanyOCRSettings);
      setMessage({ type: 'success', text: 'デフォルト設定に戻しました' });
    }
  };

  // グループタブ切り替えロジック
  const handleGroupChange = useCallback((groupId: TabGroup) => {
    setActiveGroup(groupId);
    // グループが変更されたら、そのグループの最初のタブを選択
    const group = TAB_GROUPS.find(g => g.id === groupId);
    if (group && group.tabs.length > 0) {
      setActiveTab(group.tabs[0].id);
    }
  }, []);

  const handleTabChange = useCallback((tabId: TabType) => {
    setActiveTab(tabId);
    // タブが変更されたら、そのタブが属するグループを見つけてアクティブグループを更新
    const group = TAB_GROUPS.find(g => g.tabs.some(t => t.id === tabId));
    if (group) {
      setActiveGroup(group.id);
    }
  }, []);

  // CompanyPartnerSelection用の非同期関数ラッパー
  const getCompanyPartnerSelectionsAsync = useCallback(
    async (companyId?: string) => {
      return Promise.resolve(getCompanyPartnerSelectionsByCompanyId(companyId || DEMO_COMPANY_ID));
    },
    [getCompanyPartnerSelectionsByCompanyId]
  );

  const updateCompanyPartnerSelectionAsync = useCallback(
    async (id: string, selection: Partial<CompanyPartnerSelection>) => {
      await updateCompanyPartnerSelection(id, selection);
    },
    [updateCompanyPartnerSelection]
  );

  const createCompanyPartnerSelectionAsync = useCallback(
    async (selection: Omit<CompanyPartnerSelection, 'id' | 'createdAt' | 'updatedAt'>) => {
      const newSelection: CompanyPartnerSelection = {
        ...selection,
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await addCompanyPartnerSelection(newSelection);
      return newSelection;
    },
    [addCompanyPartnerSelection]
  );

  return (
    <div className='container mx-auto py-8 px-4'>
      <PageTitle icon={CogIcon} title='システム設定' />

      {/* メッセージ表示 */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400'
              : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircleIcon className='h-5 w-5' />
          ) : (
            <ExclamationTriangleIcon className='h-5 w-5' />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* グループ化されたタブナビゲーション */}
      <div className='mb-8 space-y-4'>
        {/* グループナビゲーション */}
        <div className='border-b border-gray-200 dark:border-gray-700'>
          <nav className='flex space-x-8'>
            {TAB_GROUPS.map(group => {
              const IconComponent = group.icon;
              return (
                <button
                  key={group.id}
                  onClick={() => handleGroupChange(group.id)}
                  className={`py-3 px-1 border-b-2 font-semibold text-sm ${
                    activeGroup === group.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className='flex items-center space-x-2'>
                    <IconComponent className='h-5 w-5' />
                    <span>{group.label}</span>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* サブタブナビゲーション */}
        <div className='bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2'>
          <nav className='flex space-x-2'>
            {TAB_GROUPS.find(g => g.id === activeGroup)?.tabs.map(tab => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`py-2 px-3 rounded-md font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className='flex items-center space-x-2'>
                    <IconComponent className='h-4 w-4' />
                    <span>{tab.label}</span>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* タブコンテンツ */}
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6'>
        {activeTab === 'buffer' && (
          <BufferTimeSettingsForm
            settings={bufferSettingsState}
            onChange={handleBufferSettingsChange}
          />
        )}
        {activeTab === 'timeLimit' && (
          <div className='text-center py-12 text-gray-500'>
            <CogIcon className='h-12 w-12 mx-auto mb-4 text-gray-300' />
            <p>時間制限設定は今後のバージョンで実装予定です</p>
          </div>
        )}
        {activeTab === 'ocr' && (
          <OCRSettingsForm settings={ocrSettingsState} onChange={handleOCRSettingsChange} />
        )}
        {activeTab === 'partners' && (
          <PartnerSelectionForm
            allPartners={
              allPartnersForSettings.length > 0 ? allPartnersForSettings : businessPartners
            }
            activeCases={activeCases}
            isLoading={isLoading}
            getCompanyPartnerSelections={getCompanyPartnerSelectionsAsync}
            updateCompanyPartnerSelection={updateCompanyPartnerSelectionAsync}
            createCompanyPartnerSelection={createCompanyPartnerSelectionAsync}
          />
        )}
        {activeTab === 'vehicles' && (
          <VehicleModelSelectionForm
            vehicleModels={vehicleModels}
            manufacturers={manufacturers}
            isLoading={loading}
            getCompanyVehicleModelSettingsByCompanyId={getCompanyVehicleModelSettingsByCompanyId}
            updateCompanyVehicleModelSettings={updateCompanyVehicleModelSettings}
            addCompanyVehicleModelSettings={addCompanyVehicleModelSettings}
          />
        )}
        {activeTab === 'transportFee' && (
          <TransportFeeSettingsForm
            settings={transportFeeSettings}
            onChange={handleTransportFeeSettingsChange}
          />
        )}
        {activeTab === 'schedule' && (
          <div className='space-y-8'>
            {/* FL-114: スケジュール可視性設定 */}
            <ScheduleVisibilitySettingsForm
              visibility={scheduleVisibility}
              onChange={setScheduleVisibility}
              onSave={handleScheduleSettingsSave}
              isLoading={isLoading}
            />

            {/* FL-115: スケジュール編集権限設定 */}
            <div className='border-t border-gray-200 dark:border-gray-700 pt-8'>
              <DriverScheduleEditPermissionForm
                permission={scheduleEditPermission}
                onChange={setScheduleEditPermission}
                onSave={handleEditPermissionSave}
                isLoading={isLoading}
              />
            </div>
          </div>
        )}
        {activeTab === 'driverVisibility' && (
          <DriverVisibilitySettingsForm
            showTransportFeeToDriver={driverVisibilitySettings.showTransportFeeToDriver}
            onChange={show => setDriverVisibilitySettings({ showTransportFeeToDriver: show })}
            onSave={handleDriverVisibilitySave}
            isLoading={isLoading}
          />
        )}
        {activeTab === 'dailyReport' && (
          <DailyReportSettingsForm
            settings={dailyReportSettings}
            onChange={setDailyReportSettings}
            onSave={handleDailyReportSettingsSave}
            onSendTestEmail={handleSendTestEmail}
            onExecuteNow={handleExecuteDailyReportNow}
            isLoading={isLoading}
          />
        )}
      </div>

      {/* アクションボタン（取引先選択・車両モデル選択・陸送費設定・スケジュール設定・ドライバー表示設定・日次レポート設定タブ以外） */}
      {activeTab !== 'partners' &&
        activeTab !== 'vehicles' &&
        activeTab !== 'transportFee' &&
        activeTab !== 'schedule' &&
        activeTab !== 'driverVisibility' &&
        activeTab !== 'dailyReport' && (
          <div className='mt-8 flex justify-between'>
            <Button variant='secondary' onClick={handleReset} disabled={isLoading}>
              デフォルトに戻す
            </Button>
            <Button variant='primary' onClick={handleSave} disabled={isLoading} className='px-8'>
              {isLoading ? '保存中...' : '設定を保存'}
            </Button>
          </div>
        )}
    </div>
  );
};

// バッファ時間設定フォームコンポーネント
const BufferTimeSettingsForm: React.FC<{
  settings: BufferTimeSettings;
  onChange: (settings: Partial<BufferTimeSettings>) => void;
}> = ({ settings, onChange }) => {
  return (
    <div className='space-y-8'>
      <div>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-6'>バッファ時間設定</h3>

        {/* 案件間移動バッファ */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-8'>
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              案件間移動バッファ (分)
            </label>
            <input
              type='number'
              min='0'
              max='180'
              value={settings.betweenCasesBuffer}
              onChange={e =>
                onChange({
                  betweenCasesBuffer: parseInt(e.target.value) || 0,
                })
              }
              className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
            />
            <p className='mt-1 text-sm text-gray-500'>案件終了から次の案件開始までの移動時間</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              準備時間バッファ (分)
            </label>
            <input
              type='number'
              min='0'
              max='60'
              value={settings.dailyPreparationBuffer}
              onChange={e =>
                onChange({
                  dailyPreparationBuffer: parseInt(e.target.value) || 0,
                })
              }
              className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
            />
            <p className='mt-1 text-sm text-gray-500'>1日最初の案件前の準備時間</p>
          </div>
        </div>

        {/* 昼休み設定 */}
        <div className='mb-8'>
          <h4 className='text-md font-medium text-gray-900 dark:text-white mb-4'>昼休み時間設定</h4>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <TimeSelector
              label='開始時間'
              value={settings.lunchTimeBuffer.startTime}
              onChange={value =>
                onChange({
                  lunchTimeBuffer: {
                    ...settings.lunchTimeBuffer,
                    startTime: value,
                  },
                })
              }
              timeSettings={{
                minTime: '09:00',
                maxTime: '15:00',
                excludedHours: [],
                minimumDuration: 5,
              }}
              intervalMinutes={5}
            />
            <TimeSelector
              label='終了時間'
              value={settings.lunchTimeBuffer.endTime}
              onChange={value =>
                onChange({
                  lunchTimeBuffer: {
                    ...settings.lunchTimeBuffer,
                    endTime: value,
                  },
                })
              }
              timeSettings={{
                minTime: '09:00',
                maxTime: '15:00',
                excludedHours: [],
                minimumDuration: 5,
              }}
              intervalMinutes={5}
            />
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                バッファ時間 (分)
              </label>
              <input
                type='number'
                min='0'
                max='240'
                value={settings.lunchTimeBuffer.bufferMinutes}
                onChange={e =>
                  onChange({
                    lunchTimeBuffer: {
                      ...settings.lunchTimeBuffer,
                      bufferMinutes: parseInt(e.target.value) || 0,
                    },
                  })
                }
                className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
              />
            </div>
          </div>
          <p className='mt-2 text-sm text-gray-500'>昼休み時間前後のバッファ時間を設定します</p>
        </div>

        {/* 距離別移動バッファ */}
        <div>
          <h4 className='text-md font-medium text-gray-900 dark:text-white mb-4'>
            距離別移動バッファ設定
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                市内移動 (分)
              </label>
              <input
                type='number'
                min='0'
                max='120'
                value={settings.longDistanceBuffer.local}
                onChange={e =>
                  onChange({
                    longDistanceBuffer: {
                      ...settings.longDistanceBuffer,
                      local: parseInt(e.target.value) || 0,
                    },
                  })
                }
                className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                県内移動 (分)
              </label>
              <input
                type='number'
                min='0'
                max='120'
                value={settings.longDistanceBuffer.prefectural}
                onChange={e =>
                  onChange({
                    longDistanceBuffer: {
                      ...settings.longDistanceBuffer,
                      prefectural: parseInt(e.target.value) || 0,
                    },
                  })
                }
                className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                県外移動 (分)
              </label>
              <input
                type='number'
                min='0'
                max='180'
                value={settings.longDistanceBuffer.crossPrefecture}
                onChange={e =>
                  onChange({
                    longDistanceBuffer: {
                      ...settings.longDistanceBuffer,
                      crossPrefecture: parseInt(e.target.value) || 0,
                    },
                  })
                }
                className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
              />
            </div>
          </div>
          <p className='mt-2 text-sm text-gray-500'>移動距離に応じたバッファ時間を設定します</p>
        </div>
      </div>
    </div>
  );
};

// OCR設定フォームコンポーネント
const OCRSettingsForm: React.FC<{
  settings: CompanyOCRSettings;
  onChange: (settings: Partial<CompanyOCRSettings>) => void;
}> = ({ settings, onChange }) => {
  return (
    <div className='space-y-8'>
      <div>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-6'>OCR機能設定</h3>

        {/* OCR機能の有効/無効 */}
        <div className='mb-8'>
          <div className='flex items-center justify-between'>
            <div>
              <label className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                OCR機能
              </label>
              <p className='text-sm text-gray-500 mt-1'>画像からの文字認識機能を有効にします</p>
            </div>
            <div className='flex items-center space-x-3'>
              <span
                className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                  settings.ocrEnabled
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {settings.ocrEnabled ? '有効' : '無効'}
              </span>
              <Toggle
                checked={settings.ocrEnabled}
                onChange={checked => onChange({ ocrEnabled: checked })}
                aria-label='OCR機能を有効にする'
              />
            </div>
          </div>
        </div>

        {/* 自動抽出設定 */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-8'>
          <div className='flex items-center justify-between'>
            <div>
              <label className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                住所自動抽出
              </label>
              <p className='text-sm text-gray-500 mt-1'>住所情報を自動でフォームに設定</p>
            </div>
            <div className='flex items-center space-x-3'>
              <span
                className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                  settings.autoAddressExtraction
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {settings.autoAddressExtraction ? '有効' : '無効'}
              </span>
              <Toggle
                checked={settings.autoAddressExtraction}
                onChange={checked => onChange({ autoAddressExtraction: checked })}
                disabled={!settings.ocrEnabled}
                aria-label='住所自動抽出を有効にする'
              />
            </div>
          </div>

          <div className='flex items-center justify-between'>
            <div>
              <label className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                電話番号自動抽出
              </label>
              <p className='text-sm text-gray-500 mt-1'>電話番号を自動でフォームに設定</p>
            </div>
            <div className='flex items-center space-x-3'>
              <span
                className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                  settings.autoPhoneExtraction
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {settings.autoPhoneExtraction ? '有効' : '無効'}
              </span>
              <Toggle
                checked={settings.autoPhoneExtraction}
                onChange={checked => onChange({ autoPhoneExtraction: checked })}
                disabled={!settings.ocrEnabled}
                aria-label='電話番号自動抽出を有効にする'
              />
            </div>
          </div>
        </div>

        {/* 詳細設定 */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              信頼度閾値
            </label>
            <input
              type='number'
              min='0'
              max='1'
              step='0.1'
              value={settings.confidenceThreshold}
              onChange={e => onChange({ confidenceThreshold: parseFloat(e.target.value) || 0 })}
              disabled={!settings.ocrEnabled}
              className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50'
            />
            <p className='mt-1 text-sm text-gray-500'>抽出結果の最小信頼度 (0.0-1.0)</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              最大ファイル数
            </label>
            <input
              type='number'
              min='1'
              max='10'
              value={settings.maxFilesPerUpload}
              onChange={e => onChange({ maxFilesPerUpload: parseInt(e.target.value) || 1 })}
              disabled={!settings.ocrEnabled}
              className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50'
            />
            <p className='mt-1 text-sm text-gray-500'>同時アップロード可能な枚数</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              最大ファイルサイズ (MB)
            </label>
            <input
              type='number'
              min='1'
              max='50'
              value={settings.maxFileSizeMB}
              onChange={e => onChange({ maxFileSizeMB: parseInt(e.target.value) || 1 })}
              disabled={!settings.ocrEnabled}
              className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50'
            />
            <p className='mt-1 text-sm text-gray-500'>1ファイルあたりの最大サイズ</p>
          </div>
        </div>

        {/* 説明 */}
        <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
          <div className='flex items-start space-x-3'>
            <PhotoIcon className='h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5' />
            <div>
              <h4 className='text-sm font-medium text-blue-900 dark:text-blue-100'>
                OCR機能について
              </h4>
              <p className='text-sm text-blue-700 dark:text-blue-300 mt-1'>
                画像から住所や電話番号を自動で読み取り、案件登録フォームに自動入力する機能です。
                処理後に手動で修正することも可能です。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 取引先選択フォームコンポーネント
const PartnerSelectionForm: React.FC<{
  allPartners: BusinessPartner[];
  activeCases: Case[];
  isLoading: boolean;
  getCompanyPartnerSelections: (companyId?: string) => Promise<CompanyPartnerSelection[]>;
  updateCompanyPartnerSelection: (
    id: string,
    selection: Partial<CompanyPartnerSelection>
  ) => Promise<void>;
  createCompanyPartnerSelection: (
    selection: Omit<CompanyPartnerSelection, 'id' | 'createdAt' | 'updatedAt'>
  ) => Promise<CompanyPartnerSelection>;
}> = ({
  allPartners,
  activeCases,
  isLoading,
  getCompanyPartnerSelections,
  updateCompanyPartnerSelection,
  createCompanyPartnerSelection,
}) => {
  const [enabledPartners, setEnabledPartners] = useState<Set<string>>(new Set());
  const [partnerSelections, setPartnerSelections] = useState<CompanyPartnerSelection[]>([]);
  const [filterCategory, setFilterCategory] = useState<BusinessPartnerCategory | 'all'>('all');
  const [filterUsage, setFilterUsage] = useState<'all' | 'used' | 'unused'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuth();
  const companyId = user?.companyId;

  // 稼働中案件で使用されている取引先を判定
  const isPartnerUsedInActiveCases = useCallback(
    (partnerId: string) => {
      const usedRoles: string[] = [];
      const caseIds: string[] = [];

      activeCases.forEach(c => {
        if (c.pickupLocationId === partnerId) {
          usedRoles.push('引取先');
          caseIds.push(c.id);
        }
        if (c.deliveryLocationId === partnerId) {
          usedRoles.push('搬入先');
          caseIds.push(c.id);
        }
        if (c.billingPartnerId === partnerId) {
          usedRoles.push('請求先');
          caseIds.push(c.id);
        }
      });

      return {
        isUsed: usedRoles.length > 0,
        roles: [...new Set(usedRoles)],
        caseCount: new Set(caseIds).size,
      };
    },
    [activeCases]
  );

  // 初期データ読み込み
  useEffect(() => {
    const loadSelections = async () => {
      if (!companyId) return;

      try {
        const selections = await getCompanyPartnerSelections(companyId);
        setPartnerSelections(selections);

        // 有効な取引先のセットを構築
        const enabledSet = new Set(
          selections.filter(s => s.isEnabled).map(s => s.businessPartnerId)
        );
        setEnabledPartners(enabledSet);
      } catch (error) {
        logger.error('取引先選択設定の読み込みに失敗しました:', error);
      }
    };

    loadSelections();
  }, [companyId, getCompanyPartnerSelections]);

  const handleTogglePartner = useCallback(
    async (partnerId: string) => {
      const usageInfo = isPartnerUsedInActiveCases(partnerId);
      if (usageInfo.isUsed && enabledPartners.has(partnerId)) {
        return; // 稼働中の案件で使用中の場合は無効化しない
      }

      const isCurrentlyEnabled = enabledPartners.has(partnerId);

      try {
        if (!companyId) {
          logger.error('❌ 会社IDが未設定のため、設定を保存できません');
          return;
        }

        // 該当する取引先の選択設定を検索
        const existingSelection = partnerSelections.find(s => s.businessPartnerId === partnerId);

        if (existingSelection) {
          // 既存の選択設定を更新
          await updateCompanyPartnerSelection(existingSelection.id, {
            isEnabled: !isCurrentlyEnabled,
          });

          // ローカル状態を更新
          setPartnerSelections(prev =>
            prev.map(s =>
              s.id === existingSelection.id ? { ...s, isEnabled: !isCurrentlyEnabled } : s
            )
          );
        } else {
          // 新規選択設定を作成
          const newSelection = await createCompanyPartnerSelection({
            companyId,
            businessPartnerId: partnerId,
            isEnabled: !isCurrentlyEnabled,
            roles: [],
            priority: 999,
            billingAllowed: true,
            pickupAllowed: true,
            deliveryAllowed: true,
            usageCount: 0,
          });

          // ローカル状態に追加
          setPartnerSelections(prev => [...prev, newSelection]);
        }

        // API呼び出し成功後にenabledPartnersを更新
        const newEnabledPartners = new Set(enabledPartners);
        if (isCurrentlyEnabled) {
          newEnabledPartners.delete(partnerId);
        } else {
          newEnabledPartners.add(partnerId);
        }
        setEnabledPartners(newEnabledPartners);

        logger.info(`✅ 取引先「${partnerId}」の状態を更新しました`);
      } catch (error) {
        logger.error('❌ 取引先設定の更新に失敗しました:', error);
        // エラー時は元の状態を維持（既にenabledPartnersは変更されていないため追加処理不要）
      }
    },
    [
      companyId,
      enabledPartners,
      partnerSelections,
      isPartnerUsedInActiveCases,
      updateCompanyPartnerSelection,
      createCompanyPartnerSelection,
    ]
  );

  // ラクハイ提供データのみをフィルタリング（有効/無効設定対象）
  const filteredPartners = useMemo(() => {
    logger.info('SettingsPage - 取引先データフィルタリング:', {
      totalPartners: allPartners.length,
      rakuhaiProvided: allPartners.filter(
        p => p.dataSource === BusinessPartnerDataSource.RAKUHAI_PROVIDED
      ).length,
      custom: allPartners.filter(p => p.dataSource === BusinessPartnerDataSource.CUSTOM).length,
    });

    // 独自追加の取引先を除外（システム設定では管理対象外）
    let filtered = allPartners.filter(
      partner => partner.dataSource !== BusinessPartnerDataSource.CUSTOM
    );

    // カテゴリフィルタ
    if (filterCategory !== 'all') {
      filtered = filtered.filter(partner => partner.category === filterCategory);
    }

    // 使用状況フィルタ
    if (filterUsage !== 'all') {
      filtered = filtered.filter(partner => {
        const usageInfo = isPartnerUsedInActiveCases(partner.id);
        return filterUsage === 'used' ? usageInfo.isUsed : !usageInfo.isUsed;
      });
    }

    // 検索クエリフィルタ
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        partner =>
          partner.name.toLowerCase().includes(query) ||
          partner.defaultAddress.toLowerCase().includes(query) ||
          partner.defaultPhone.includes(query)
      );
    }

    return filtered;
  }, [allPartners, filterCategory, filterUsage, searchQuery, isPartnerUsedInActiveCases]);

  // 選択状況の統計
  const selectionStats = useMemo(() => {
    const total = filteredPartners.length;
    const selected = filteredPartners.filter(partner => enabledPartners.has(partner.id)).length;
    const usedInActiveCases = filteredPartners.filter(
      partner => isPartnerUsedInActiveCases(partner.id).isUsed
    ).length;

    return { total, selected, usedInActiveCases };
  }, [filteredPartners, enabledPartners, isPartnerUsedInActiveCases]);

  const getCategoryLabel = useCallback((category: BusinessPartnerCategory): string => {
    switch (category) {
      case BusinessPartnerCategory.DEALERSHIP:
        return '売買・買取店';
      case BusinessPartnerCategory.AUCTION:
        return 'オークション会場';
      case BusinessPartnerCategory.OTHER:
        return 'その他';
      default:
        return '不明';
    }
  }, []);

  const getCategoryBadge = useCallback((category: BusinessPartnerCategory): string => {
    switch (category) {
      case BusinessPartnerCategory.DEALERSHIP:
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case BusinessPartnerCategory.AUCTION:
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
      case BusinessPartnerCategory.OTHER:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  }, []);

  return (
    <div className='space-y-6'>
      <div>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
          ラクハイ提供取引先設定
        </h3>
        <p className='text-sm text-gray-600 dark:text-gray-400 mb-6'>
          ラクハイが提供する取引先は初期状態で有効です。必要に応じて無効にすることで、配車時の選択肢から除外できます。
          <br />
          <span className='text-yellow-600 dark:text-yellow-400 font-medium'>
            ※ 稼働中の案件で使用されている取引先は無効化できません。
          </span>
        </p>

        {/* フィルタ・検索 */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              カテゴリで絞り込み
            </label>
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value as BusinessPartnerCategory | 'all')}
              className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
            >
              <option value='all'>すべてのカテゴリ</option>
              <option value={BusinessPartnerCategory.DEALERSHIP}>売買・買取店</option>
              <option value={BusinessPartnerCategory.AUCTION}>オークション会場</option>
              <option value={BusinessPartnerCategory.OTHER}>その他</option>
            </select>
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              使用状況で絞り込み
            </label>
            <select
              value={filterUsage}
              onChange={e => setFilterUsage(e.target.value as 'all' | 'used' | 'unused')}
              className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
            >
              <option value='all'>すべて</option>
              <option value='used'>稼働中案件で使用中</option>
              <option value='unused'>未使用</option>
            </select>
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              名前・住所で検索
            </label>
            <input
              type='text'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder='取引先名または住所を入力'
              className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
            />
          </div>
        </div>

        {/* 統計情報と一括操作 */}
        <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
            <div className='flex flex-wrap items-center gap-4 text-sm'>
              <span className='text-gray-600 dark:text-gray-400'>
                表示中: <span className='font-medium'>{selectionStats.total}件</span>
              </span>
              <span className='px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 rounded-full text-xs font-medium'>
                有効: {selectionStats.selected}件
              </span>
              <span className='px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 rounded-full text-xs font-medium'>
                使用中: {selectionStats.usedInActiveCases}件
              </span>
            </div>
          </div>
        </div>

        {/* 取引先リスト */}
        <div className='space-y-4'>
          <div className='max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg'>
            {isLoading ? (
              <div className='p-8 text-center text-gray-500 dark:text-gray-400'>
                <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600'></div>
                <p className='mt-2'>読み込み中...</p>
              </div>
            ) : filteredPartners.length === 0 ? (
              <div className='p-8 text-center text-gray-500 dark:text-gray-400'>
                <BuildingOffice2Icon className='h-12 w-12 mx-auto mb-4 text-gray-300' />
                <p>条件に一致する取引先が見つかりませんでした</p>
              </div>
            ) : (
              <div className='divide-y divide-gray-200 dark:divide-gray-700'>
                {filteredPartners.map(partner => {
                  const isEnabled = enabledPartners.has(partner.id);
                  const partnerName = partner.displayName || partner.name;
                  const partnerAddress = partner.defaultAddress;
                  const partnerPhone = partner.defaultPhone;
                  const partnerContact = partner.defaultContactPerson;
                  const usageInfo = isPartnerUsedInActiveCases(partner.id);
                  const canToggle = !usageInfo.isUsed;

                  return (
                    <div
                      key={partner.id}
                      className='p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
                    >
                      <div className='flex items-start justify-between'>
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center space-x-3 mb-2'>
                            <BuildingOffice2Icon className='h-5 w-5 text-blue-500 flex-shrink-0' />
                            <h4 className='text-sm font-medium text-gray-900 dark:text-white truncate'>
                              {partnerName}
                            </h4>
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryBadge(
                                partner.category
                              )}`}
                            >
                              <TagIcon className='h-3 w-3 inline mr-1' />
                              {getCategoryLabel(partner.category)}
                            </span>
                          </div>
                          <div className='text-xs text-gray-500 dark:text-gray-400 space-y-1'>
                            <p className='flex items-center'>
                              <span className='w-12 font-medium'>住所:</span>
                              <span className='truncate'>{partnerAddress}</span>
                            </p>
                            <p className='flex items-center'>
                              <span className='w-12 font-medium'>電話:</span>
                              <span>{partnerPhone}</span>
                            </p>
                            <p className='flex items-center'>
                              <span className='w-12 font-medium'>担当:</span>
                              <span>{partnerContact}</span>
                            </p>
                          </div>

                          {/* 使用状況表示 */}
                          {usageInfo.isUsed && (
                            <div className='mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md'>
                              <div className='flex items-center space-x-2'>
                                <ExclamationTriangleIcon className='h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0' />
                                <div className='text-xs text-yellow-800 dark:text-yellow-200'>
                                  <p className='font-medium'>稼働中の案件で使用中</p>
                                  <p>
                                    {usageInfo.caseCount}件の案件で{usageInfo.roles.join('・')}
                                    として使用
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* 有効/無効ステータスとトグルスイッチ */}
                        <div className='ml-4 flex items-center space-x-3'>
                          <span
                            className={`text-sm font-medium ${
                              usageInfo.isUsed
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : isEnabled
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {usageInfo.isUsed ? '使用中' : isEnabled ? '有効' : '無効'}
                          </span>
                          {/* トグルスイッチとバルーンメッセージのコンテナ */}
                          <div className='relative'>
                            <Toggle
                              checked={isEnabled}
                              onChange={() => handleTogglePartner(partner.id)}
                              disabled={!canToggle}
                              size='md'
                              aria-label={`${partner.name}を${isEnabled ? '無効' : '有効'}にする`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* 説明 */}
        <div className='mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
          <div className='flex items-start space-x-3'>
            <BuildingOffice2Icon className='h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5' />
            <div>
              <h4 className='text-sm font-medium text-blue-900 dark:text-blue-100'>
                ラクハイ提供取引先について
              </h4>
              <p className='text-sm text-blue-700 dark:text-blue-300 mt-1'>
                各取引先は初期状態で有効です。右側のトグルスイッチをOFFにすると無効になり、配車時の選択肢に表示されなくなります。設定はいつでも変更可能です。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 車両モデル選択フォームコンポーネント
const VehicleModelSelectionForm: React.FC<{
  vehicleModels: VehicleModel[];
  manufacturers: Manufacturer[];
  isLoading: boolean;
  getCompanyVehicleModelSettingsByCompanyId: (
    companyId: string
  ) => CompanyVehicleModelSettings | null;
  updateCompanyVehicleModelSettings: (
    id: string,
    settings: Partial<CompanyVehicleModelSettings>
  ) => Promise<void>;
  addCompanyVehicleModelSettings: (settings: CompanyVehicleModelSettings) => Promise<void>;
}> = ({
  vehicleModels,
  manufacturers,
  isLoading,
  getCompanyVehicleModelSettingsByCompanyId,
  updateCompanyVehicleModelSettings,
  addCompanyVehicleModelSettings,
}) => {
  const [enabledModelIds, setEnabledModelIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterManufacturer, setFilterManufacturer] = useState<string>('all');
  const [selectedVehicleModel, setSelectedVehicleModel] = useState<VehicleModel | null>(null);
  const companyId = DEMO_COMPANY_ID;

  // 初期データ読み込み
  useEffect(() => {
    const loadSettings = async () => {
      const settings = getCompanyVehicleModelSettingsByCompanyId(companyId);
      if (settings?.enabledModelIds) {
        setEnabledModelIds(settings.enabledModelIds);
      }
    };
    loadSettings();
  }, [companyId, getCompanyVehicleModelSettingsByCompanyId]);

  const handleToggleModel = useCallback(
    async (modelId: string) => {
      const isCurrentlyEnabled = enabledModelIds.includes(modelId);
      const newEnabledIds = isCurrentlyEnabled
        ? enabledModelIds.filter(id => id !== modelId)
        : [...enabledModelIds, modelId];

      setEnabledModelIds(newEnabledIds);

      try {
        const existingSettings = getCompanyVehicleModelSettingsByCompanyId(companyId);
        if (existingSettings) {
          await updateCompanyVehicleModelSettings(existingSettings.id, {
            enabledModelIds: newEnabledIds,
          });
        } else {
          await addCompanyVehicleModelSettings({
            id: crypto.randomUUID(),
            companyId,
            enabledModelIds: newEnabledIds,
            dataSourcePreferences: { allowRakuhaiProvided: true, allowCustom: true },
            countryPreferences: { allowDomestic: true, allowForeign: true },
            powertrainPreferences: {
              allowGasoline: true,
              allowDiesel: true,
              allowHybrid: true,
              allowElectric: true,
              allowOther: true,
            },
            tagPreferences: {
              allowImmobilized: true,
              allowLowHeight: true,
              allowWideBody: true,
              allowWelfare: true,
              allowCamping: true,
              allowClassic: true,
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        }
      } catch (error) {
        logger.error('Failed to update vehicle model settings:', error);
        setEnabledModelIds(enabledModelIds);
      }
    },
    [
      companyId,
      enabledModelIds,
      getCompanyVehicleModelSettingsByCompanyId,
      updateCompanyVehicleModelSettings,
      addCompanyVehicleModelSettings,
    ]
  );

  // 車両モデル詳細表示ハンドラー
  const handleVehicleModelClick = useCallback((model: VehicleModel) => {
    setSelectedVehicleModel(model);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedVehicleModel(null);
  }, []);

  // フィルタリング処理
  const filteredModels = useMemo(() => {
    return vehicleModels.filter(model => {
      // カテゴリフィルタ（暫定的に無効化）
      // TODO: vehicleCategoryIdと車両カテゴリマスタを使用したフィルタリングに変更
      // if (filterCategory !== 'all' && model.vehicleCategoryId !== filterCategory) {
      //   return false;
      // }

      // メーカーフィルタ
      if (filterManufacturer !== 'all' && model.manufacturerId !== filterManufacturer) {
        return false;
      }

      // 検索クエリ
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const manufacturer = manufacturers.find(m => m.id === model.manufacturerId);
        return (
          model.name.toLowerCase().includes(query) ||
          manufacturer?.name.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [vehicleModels, manufacturers, filterCategory, filterManufacturer, searchQuery]);

  // 件数計算
  const enabledCount = enabledModelIds.length;
  const displayedCount = filteredModels.length;

  return (
    <div className='space-y-6'>
      <div className='bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4'>
        <p className='text-sm text-blue-800 dark:text-blue-300'>
          案件作成時に選択可能な車両モデルを設定します。
        </p>
      </div>

      {/* フィルタ・検索 */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            カテゴリで絞り込み
          </label>
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
          >
            <option value='all'>すべてのカテゴリ</option>
            <option value='light'>軽自動車</option>
            <option value='standard'>普通車</option>
            <option value='truck'>トラック</option>
          </select>
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            メーカーで絞り込み
          </label>
          <select
            value={filterManufacturer}
            onChange={e => setFilterManufacturer(e.target.value)}
            className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
          >
            <option value='all'>すべてのメーカー</option>
            {manufacturers.map(m => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            モデル名で検索
          </label>
          <input
            type='text'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder='車両モデル名を入力'
            className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
          />
        </div>
      </div>

      {/* 車両モデルリスト */}
      <div className='space-y-4'>
        <div className='flex justify-between items-center'>
          <span className='text-sm text-gray-600 dark:text-gray-400'>
            表示中: {displayedCount}件 / 有効: {enabledCount}件
          </span>
        </div>
        <div className='max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg'>
          {isLoading ? (
            <div className='p-8 text-center text-gray-500 dark:text-gray-400'>
              <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600'></div>
              <p className='mt-2'>読み込み中...</p>
            </div>
          ) : filteredModels.length === 0 ? (
            <div className='p-8 text-center text-gray-500 dark:text-gray-400'>
              <TagIcon className='h-12 w-12 mx-auto mb-4 text-gray-300' />
              <p>条件に一致する車両モデルが見つかりませんでした</p>
            </div>
          ) : (
            <div className='divide-y divide-gray-200 dark:divide-gray-700'>
              {filteredModels.map(model => {
                const isEnabled = enabledModelIds.includes(model.id);
                const manufacturer = manufacturers.find(m => m.id === model.manufacturerId);

                return (
                  <div
                    key={model.id}
                    className='p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
                  >
                    <div className='flex items-start justify-between'>
                      <div
                        className='flex-1 min-w-0 cursor-pointer'
                        onClick={() => handleVehicleModelClick(model)}
                      >
                        <div className='flex items-center space-x-3 mb-2'>
                          <TagIcon className='h-5 w-5 text-blue-500 flex-shrink-0' />
                          <h4 className='text-sm font-medium text-gray-900 dark:text-white'>
                            {manufacturer?.name} {model.name}
                          </h4>
                          {/* パワートレイン表示 */}
                          {model.powertrain && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPowertrainColorClasses(model.powertrain)}`}
                            >
                              {getPowertrainLabel(model.powertrain)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className='ml-4 flex items-center space-x-3'>
                        <span
                          className={`text-sm font-medium ${
                            isEnabled
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {isEnabled ? '有効' : '無効'}
                        </span>
                        <Toggle
                          checked={isEnabled}
                          onChange={() => handleToggleModel(model.id)}
                          size='md'
                          aria-label={`${model.name}を${isEnabled ? '無効' : '有効'}にする`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 車両モデル詳細モーダル */}
      {selectedVehicleModel && (
        <VehicleModelDetail
          vehicleModel={selectedVehicleModel}
          onClose={handleCloseDetail}
          isModal={true}
        />
      )}
    </div>
  );
};

// 日次レポート設定フォームコンポーネント
const DailyReportSettingsForm: React.FC<{
  settings: {
    reportType: 'case_list' | 'driver_schedule' | 'combined';
    sendTime: string;
    sendToEmails: string[];
    includeCsv: boolean;
    includePdf: boolean;
    dateRangeDays: number;
    isActive: boolean;
    emailSubjectTemplate?: string;
    emailBodyTemplate?: string;
  };
  onChange: (settings: any) => void;
  onSave: () => Promise<void>;
  onSendTestEmail: (email: string) => Promise<void>;
  onExecuteNow?: () => Promise<void>;
  isLoading: boolean;
}> = ({ settings, onChange, onSave, onSendTestEmail, onExecuteNow, isLoading }) => {
  const [newEmailAddress, setNewEmailAddress] = useState('');
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const { isEnabled: isDemoMode } = useDemoMode();

  const handleAddEmail = () => {
    if (newEmailAddress && !settings.sendToEmails.includes(newEmailAddress)) {
      onChange({
        ...settings,
        sendToEmails: [...settings.sendToEmails, newEmailAddress],
      });
      setNewEmailAddress('');
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    onChange({
      ...settings,
      sendToEmails: settings.sendToEmails.filter(email => email !== emailToRemove),
    });
  };

  const handleTestEmail = () => {
    if (testEmailAddress) {
      onSendTestEmail(testEmailAddress);
    }
  };

  return (
    <div className='space-y-8'>
      <div>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-6'>日次レポート設定</h3>
        <p className='text-sm text-gray-600 dark:text-gray-400 mb-6'>
          毎日指定の時間に案件一覧と陸送員スケジュールをメールで送信します。システム障害時の業務継続に役立つPDF版も含まれます。
        </p>

        {/* 機能有効化 */}
        <div className='mb-8'>
          <div className='flex items-center justify-between'>
            <div>
              <label className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                日次レポート機能
              </label>
              <p className='text-sm text-gray-500 mt-1'>毎日自動でレポートメールを送信します</p>
            </div>
            <div className='flex items-center space-x-3'>
              <span
                className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                  settings.isActive
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {settings.isActive ? '有効' : '無効'}
              </span>
              <Toggle
                checked={settings.isActive}
                onChange={checked => onChange({ ...settings, isActive: checked })}
                aria-label='日次レポート機能を有効にする'
              />
            </div>
          </div>
        </div>

        {/* レポート種別 */}
        <div className='mb-8'>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>
            レポート種別
          </label>
          <div className='space-y-3'>
            {[
              {
                value: 'case_list',
                label: '案件一覧のみ',
                description: '案件データのみをCSV/PDF形式で送信',
              },
              {
                value: 'driver_schedule',
                label: '陸送員スケジュールのみ',
                description: '陸送員の出勤予定のみをCSV/PDF形式で送信',
              },
              {
                value: 'combined',
                label: '統合レポート',
                description: '案件と陸送員スケジュールの両方を送信（推奨）',
              },
            ].map(option => (
              <label key={option.value} className='flex items-start space-x-3'>
                <input
                  type='radio'
                  name='reportType'
                  value={option.value}
                  checked={settings.reportType === option.value}
                  onChange={e => onChange({ ...settings, reportType: e.target.value })}
                  className='mt-1 form-radio h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500'
                />
                <div>
                  <div className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                    {option.label}
                  </div>
                  <div className='text-xs text-gray-500'>{option.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* 送信時刻 */}
        <div className='mb-8'>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            送信時刻
          </label>
          <input
            type='time'
            value={settings.sendTime}
            onChange={e => onChange({ ...settings, sendTime: e.target.value })}
            className='block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
          />
          <p className='mt-1 text-sm text-gray-500'>毎日この時刻にレポートを送信します</p>
        </div>

        {/* ファイル形式 */}
        <div className='mb-8'>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>
            添付ファイル形式
          </label>
          <div className='space-y-3'>
            <div className='flex items-center justify-between'>
              <div>
                <span className='text-sm text-gray-700 dark:text-gray-300'>CSV形式</span>
                <p className='text-xs text-gray-500'>Excelで開けるデータ形式</p>
              </div>
              <Toggle
                checked={settings.includeCsv}
                onChange={checked => onChange({ ...settings, includeCsv: checked })}
                aria-label='CSV形式を含める'
              />
            </div>
            <div className='flex items-center justify-between'>
              <div>
                <span className='text-sm text-gray-700 dark:text-gray-300'>PDF形式</span>
                <p className='text-xs text-gray-500'>印刷・手動配車用（災害対策）</p>
              </div>
              <Toggle
                checked={settings.includePdf}
                onChange={checked => onChange({ ...settings, includePdf: checked })}
                aria-label='PDF形式を含める'
              />
            </div>
          </div>
        </div>

        {/* 対象期間 */}
        <div className='mb-8'>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            レポート対象期間
          </label>
          <select
            value={settings.dateRangeDays}
            onChange={e => onChange({ ...settings, dateRangeDays: parseInt(e.target.value) })}
            className='block w-48 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
          >
            <option value={1}>当日のみ</option>
            <option value={2}>当日〜翌日</option>
            <option value={3}>当日〜2日後</option>
            <option value={7}>今後7日間</option>
          </select>
          <p className='mt-1 text-sm text-gray-500'>レポートに含める案件・スケジュールの期間</p>
        </div>

        {/* 送信先メールアドレス */}
        <div className='mb-8'>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>
            送信先メールアドレス
          </label>

          {/* 登録済みアドレス一覧 */}
          {settings.sendToEmails.length > 0 && (
            <div className='mb-4 space-y-2'>
              {settings.sendToEmails.map((email, index) => (
                <div
                  key={index}
                  className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg'
                >
                  <span className='text-sm text-gray-700 dark:text-gray-300'>{email}</span>
                  <Button
                    variant='secondary'
                    size='sm'
                    onClick={() => handleRemoveEmail(email)}
                    className='text-red-600 hover:text-red-700'
                  >
                    削除
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* 新しいアドレス追加 */}
          <div className='flex space-x-3'>
            <input
              type='email'
              value={newEmailAddress}
              onChange={e => setNewEmailAddress(e.target.value)}
              placeholder='example@company.com'
              className='flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
            />
            <Button variant='secondary' onClick={handleAddEmail} disabled={!newEmailAddress}>
              追加
            </Button>
          </div>
          <p className='mt-1 text-sm text-gray-500'>
            管理者・配車担当者のメールアドレスを登録してください
          </p>
        </div>

        {/* テストメール送信 */}
        <div className='mb-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg'>
          <h4 className='text-sm font-medium text-blue-900 dark:text-blue-100 mb-3'>
            テストメール送信
          </h4>

          {isDemoMode && (
            <div className='mb-4'>
              <FeatureDemoNotice feature={DemoFeature.EMAIL_SEND} />
            </div>
          )}

          <div className='flex space-x-3'>
            <input
              type='email'
              value={testEmailAddress}
              onChange={e => setTestEmailAddress(e.target.value)}
              placeholder='テスト送信先アドレス'
              className='flex-1 rounded-md border-blue-300 dark:border-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-blue-800/50 dark:text-white'
              disabled={isDemoMode}
            />
            <Button
              variant='primary'
              onClick={handleTestEmail}
              disabled={!testEmailAddress || isLoading || isDemoMode}
            >
              送信テスト
            </Button>
          </div>
          <p className='mt-1 text-xs text-blue-700 dark:text-blue-300'>
            設定前に実際にメールが届くかテストできます
          </p>
        </div>

        {/* キュー処理機能は削除済み（シンプルアーキテクチャでは即座にメール送信されるため不要） */}

        {/* 手動実行 */}
        {onExecuteNow && settings.isActive && (
          <div className='mb-8 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg'>
            <h4 className='text-sm font-medium text-green-900 dark:text-green-100 mb-3'>
              手動実行
            </h4>

            {isDemoMode && (
              <div className='mb-4'>
                <FeatureDemoNotice feature={DemoFeature.EMAIL_SEND} />
              </div>
            )}

            <p className='text-xs text-green-700 dark:text-green-300 mb-4'>
              設定された内容で今すぐ日次レポートを送信します。定期実行とは別に、必要に応じて手動で実行できます。
            </p>
            <Button
              variant='primary'
              onClick={onExecuteNow}
              disabled={isLoading || settings.sendToEmails.length === 0 || isDemoMode}
              className='bg-green-600 hover:bg-green-700 text-white'
            >
              {isLoading ? '実行中...' : '今すぐ実行'}
            </Button>
            {settings.sendToEmails.length === 0 && !isDemoMode && (
              <p className='mt-2 text-xs text-orange-600 dark:text-orange-400'>
                ※ 送信先メールアドレスを設定してから実行してください
              </p>
            )}
          </div>
        )}

        {/* メールテンプレート（折りたたみ可能な高度な設定） */}
        <details className='mb-8'>
          <summary className='cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'>
            高度な設定（メールテンプレート）
          </summary>
          <div className='mt-4 space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                メール件名テンプレート
              </label>
              <input
                type='text'
                value={
                  settings.emailSubjectTemplate ||
                  '[ラクハイ] 日次業務レポート - {{date}} ({{company_name}})'
                }
                onChange={e => onChange({ ...settings, emailSubjectTemplate: e.target.value })}
                className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
              />
              <p className='mt-1 text-xs text-gray-500'>
                使用可能な変数: {`{{date}}`}, {`{{company_name}}`}
              </p>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                メール本文テンプレート
              </label>
              <textarea
                rows={8}
                value={
                  settings.emailBodyTemplate ||
                  `{{company_name}} 様

本日の業務レポートをお送りいたします。

添付ファイル:
- 案件一覧.csv: {{date_from}}〜{{date_to}}の全案件データ
- 陸送員スケジュール.csv: {{date_from}}〜{{date_to}}の出勤予定
- 業務継続用レポート.pdf: 印刷対応版（A4サイズ）

【システム障害時の対応】
添付ファイルを印刷し、手動配車の参考資料としてご活用ください。

ラクハイシステム`
                }
                onChange={e => onChange({ ...settings, emailBodyTemplate: e.target.value })}
                className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
              />
              <p className='mt-1 text-xs text-gray-500'>
                使用可能な変数: {`{{company_name}}`}, {`{{date}}`}, {`{{date_from}}`},{' '}
                {`{{date_to}}`}
              </p>
            </div>
          </div>
        </details>

        {/* 保存ボタン */}
        <div className='flex justify-end space-x-3'>
          <Button variant='primary' onClick={onSave} disabled={isLoading} className='px-8'>
            {isLoading ? '保存中...' : '設定を保存'}
          </Button>
        </div>

        {/* 説明・注意事項 */}
        <div className='mt-8 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4'>
          <div className='flex items-start space-x-3'>
            <ExclamationTriangleIcon className='h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5' />
            <div>
              <h4 className='text-sm font-medium text-yellow-900 dark:text-yellow-100'>重要事項</h4>
              <ul className='text-sm text-yellow-800 dark:text-yellow-200 mt-1 space-y-1'>
                <li>• PDF版は災害時・システム障害時の手動配車用として設計されています</li>
                <li>• 毎日指定時刻に自動送信されるため、メールアドレスを正確に設定してください</li>
                <li>• テスト送信で動作確認を行ってから本格運用を開始してください</li>
                <li>• 送信履歴は別途ログ画面で確認できます</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 陸送費設定フォームコンポーネント
const TransportFeeSettingsForm: React.FC<{
  settings: TransportFeeSettings;
  onChange: (settings: Partial<TransportFeeSettings>) => void;
}> = ({ settings, onChange }) => {
  return (
    <div className='space-y-8'>
      <div>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-6'>陸送費設定</h3>
        <p className='text-sm text-gray-600 dark:text-gray-400 mb-6'>
          案件登録時の陸送費のデフォルト値を設定します。ここで設定した値は、新規案件登録時に自動で反映されますが、案件ごとに上書きも可能です。
        </p>

        {/* 料金計算方式の選択 */}
        <div className='mb-8'>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            料金計算方式
          </label>
          <div className='flex space-x-4'>
            {(['fixed', 'distance_based', 'vehicle_type_based'] as const).map(method => (
              <label key={method} className='flex items-center'>
                <input
                  type='radio'
                  name='calculation_method'
                  value={method}
                  checked={settings.calculation_method === method}
                  onChange={e =>
                    onChange({
                      calculation_method: e.target.value as TransportFeeCalculationMethod,
                    })
                  }
                  className='form-radio h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500'
                />
                <span className='ml-2 text-sm text-gray-700 dark:text-gray-300'>
                  {
                    {
                      fixed: '固定料金',
                      distance_based: '距離別料金',
                      vehicle_type_based: '車種別料金',
                    }[method]
                  }
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* 固定料金設定 */}
        {settings.calculation_method === 'fixed' && (
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              固定陸送費 (円)
            </label>
            <input
              type='number'
              min='0'
              value={settings.fixed_fee || 0}
              onChange={e => onChange({ fixed_fee: parseInt(e.target.value) || 0 })}
              className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white'
            />
            <p className='mt-1 text-sm text-gray-500'>全ての案件に適用される一律の陸送費です。</p>
          </div>
        )}

        {/* 距離別料金設定 (Placeholder) */}
        {settings.calculation_method === 'distance_based' && (
          <div className='text-center py-12 text-gray-500'>
            <p>距離別料金設定は今後のバージョンで実装予定です</p>
          </div>
        )}

        {/* 車種別料金設定 (Placeholder) */}
        {settings.calculation_method === 'vehicle_type_based' && (
          <div className='text-center py-12 text-gray-500'>
            <p>車種別料金設定は今後のバージョンで実装予定です</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
